/**
 * Shared error classification for API calls (axios-based).
 * Mirrors flutter/lib/utils/app_error.dart so both clients explain failures
 * the same way instead of treating every error as "something went wrong".
 */

export const ErrorKind = {
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  UNAUTHORIZED: 'unauthorized',
  FORBIDDEN: 'forbidden',
  NOT_FOUND: 'notFound',
  VALIDATION: 'validation',
  SERVER: 'server',
  UNKNOWN: 'unknown',
};

const TITLES = {
  [ErrorKind.NETWORK]: 'No Internet Connection',
  [ErrorKind.TIMEOUT]: 'Request Timed Out',
  [ErrorKind.UNAUTHORIZED]: 'Session Expired',
  [ErrorKind.FORBIDDEN]: 'Access Denied',
  [ErrorKind.NOT_FOUND]: 'Not Found',
  [ErrorKind.VALIDATION]: 'Check Your Input',
  [ErrorKind.SERVER]: 'Server Error',
  [ErrorKind.UNKNOWN]: 'Something Went Wrong',
};

const DEFAULT_MESSAGES = {
  [ErrorKind.NETWORK]: 'No internet connection. Check your network and try again.',
  [ErrorKind.TIMEOUT]: 'That took too long to respond. Please try again.',
  [ErrorKind.UNAUTHORIZED]: 'Your session has expired. Please log in again.',
  [ErrorKind.FORBIDDEN]: 'You do not have permission to do that.',
  [ErrorKind.NOT_FOUND]: 'We could not find what you were looking for.',
  [ErrorKind.VALIDATION]: 'Please check the information you entered and try again.',
  [ErrorKind.SERVER]: 'Our servers are having trouble right now. Please try again shortly.',
  [ErrorKind.UNKNOWN]: 'Something went wrong. Please try again.',
};

export function titleFor(kind) {
  return TITLES[kind] || TITLES[ErrorKind.UNKNOWN];
}

export function defaultMessageFor(kind) {
  return DEFAULT_MESSAGES[kind] || DEFAULT_MESSAGES[ErrorKind.UNKNOWN];
}

/** Re-tapping the same action is worth offering, unless the problem is the input itself or a hard block. */
export function isRetryable(kind) {
  return kind !== ErrorKind.VALIDATION && kind !== ErrorKind.FORBIDDEN;
}

function extractDetail(data) {
  if (!data || typeof data !== 'object') return null;
  const detail = data.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  if (Array.isArray(detail) && detail.length && detail[0]?.msg) return String(detail[0].msg);
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  return null;
}

/** Classifies an HTTP status code (+ optional backend detail) into a { kind, message }. */
export function classifyStatus(status, detail) {
  switch (status) {
    case 401:
      return { kind: ErrorKind.UNAUTHORIZED, message: detail || DEFAULT_MESSAGES[ErrorKind.UNAUTHORIZED] };
    case 403:
      return { kind: ErrorKind.FORBIDDEN, message: detail || DEFAULT_MESSAGES[ErrorKind.FORBIDDEN] };
    case 404:
      return { kind: ErrorKind.NOT_FOUND, message: detail || DEFAULT_MESSAGES[ErrorKind.NOT_FOUND] };
    case 400:
    case 422:
      return { kind: ErrorKind.VALIDATION, message: detail || DEFAULT_MESSAGES[ErrorKind.VALIDATION] };
    case 413:
      return {
        kind: ErrorKind.VALIDATION,
        message: detail || 'That photo is too large (max 3.5 MB). Try a closer shot of the meter display.',
      };
    case 429:
      return {
        kind: ErrorKind.VALIDATION,
        message: detail || 'Too many attempts. Please wait a moment and try again.',
      };
    case 504:
      return { kind: ErrorKind.TIMEOUT, message: detail || DEFAULT_MESSAGES[ErrorKind.TIMEOUT] };
    default:
      if (status >= 500) {
        return { kind: ErrorKind.SERVER, message: detail || DEFAULT_MESSAGES[ErrorKind.SERVER] };
      }
      return { kind: ErrorKind.UNKNOWN, message: detail || `Something went wrong (code ${status}).` };
  }
}

/** Best-effort classification of a plain string message (e.g. a stored error string). */
export function classifyMessage(message) {
  if (!message || typeof message !== 'string') {
    return { kind: ErrorKind.UNKNOWN, message: DEFAULT_MESSAGES[ErrorKind.UNKNOWN] };
  }
  const lower = message.toLowerCase();
  let kind;
  if (
    lower.includes('cannot reach') ||
    lower.includes('connection error') ||
    lower.includes('connection refused') ||
    lower.includes('network error') ||
    lower.includes('failed host lookup') ||
    lower.includes('network is unreachable') ||
    lower.includes('no internet')
  ) {
    kind = ErrorKind.NETWORK;
  } else if (lower.includes('timeout') || lower.includes('timed out')) {
    kind = ErrorKind.TIMEOUT;
  } else if (
    (lower.includes('session') && lower.includes('expired')) ||
    lower.includes('unauthorized') ||
    lower.includes('not authenticated') ||
    lower.includes('invalid token') ||
    lower.includes('log in again')
  ) {
    kind = ErrorKind.UNAUTHORIZED;
  } else if (
    lower.includes('locked') ||
    lower.includes('permission') ||
    lower.includes('denied') ||
    lower.includes('forbidden')
  ) {
    kind = ErrorKind.FORBIDDEN;
  } else if (lower.includes('not found')) {
    kind = ErrorKind.NOT_FOUND;
  } else if (
    lower.includes('internal server') ||
    lower.includes('server error') ||
    lower.includes('gateway') ||
    lower.includes('unavailable')
  ) {
    kind = ErrorKind.SERVER;
  } else {
    kind = ErrorKind.VALIDATION;
  }

  // Raw JS/axios error dumps ("AxiosError: Network Error", stack traces, etc.)
  // aren't fit for a user to read — swap them for a clean canonical message.
  const looksRaw =
    lower.includes('error:') && (lower.includes('axioserror') || lower.includes(' at ')) ||
    lower.startsWith('[object') ||
    lower.includes('undefined is not');
  return { kind, message: looksRaw ? DEFAULT_MESSAGES[kind] : message };
}

/**
 * Classifies a caught error from an axios call (or any thrown error) into a
 * plain `{ kind, message, statusCode }` object — the single entry point
 * every page/hook should funnel its catch blocks through.
 */
export function classifyError(error) {
  if (error && (error.isAxiosError || error.response || error.request)) {
    if (error.response) {
      const detail = extractDetail(error.response.data);
      const { kind, message } = classifyStatus(error.response.status, detail);
      return { kind, message, statusCode: error.response.status };
    }
    if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
      return { kind: ErrorKind.TIMEOUT, message: DEFAULT_MESSAGES[ErrorKind.TIMEOUT] };
    }
    return { kind: ErrorKind.NETWORK, message: DEFAULT_MESSAGES[ErrorKind.NETWORK] };
  }
  if (typeof error === 'string') return classifyMessage(error);
  if (error instanceof Error) return classifyMessage(error.message);
  return { kind: ErrorKind.UNKNOWN, message: DEFAULT_MESSAGES[ErrorKind.UNKNOWN] };
}

/** Classifies whatever an API call returned as an error string (may be null/empty). */
export function classifyApiResult(error, fallback = DEFAULT_MESSAGES[ErrorKind.UNKNOWN]) {
  if (!error || !String(error).trim()) {
    return { kind: ErrorKind.UNKNOWN, message: fallback };
  }
  return classifyMessage(String(error));
}
